/**
 * @zendir/ui - Flex Layout Primitive
 *
 * Flexbox container with theme-aware spacing and responsive direction/alignment.
 * The workhorse layout component for horizontal and vertical arrangements.
 *
 * Astro UX Compliance:
 * - 4px grid spacing tokens
 * - Responsive gap for mobile → mission control
 *
 * @example
 * ```tsx
 * // Horizontal row with centered items
 * <Flex gap="md" align="center">
 *   <StatusIndicator status="normal" />
 *   <Body1>All Systems Nominal</Body1>
 * </Flex>
 *
 * // Vertical column, responsive gap
 * <Flex direction="column" gap={['sm', 'md', 'lg']}>
 *   <TelemetryCard />
 *   <LinkBudgetCard />
 * </Flex>
 *
 * // Space between items
 * <Flex justify="space-between" align="center">
 *   <H3>Telemetry</H3>
 *   <Button variant="secondary">Refresh</Button>
 * </Flex>
 * ```
 */

import React, { memo, forwardRef } from "react";
import { useTheme } from "../../theme";
import { useBreakpoint } from "./useBreakpoint";
import { resolveResponsiveSpacing, resolveResponsive } from "./responsive";
import type { ResponsiveValue } from "./useBreakpoint";
import type { SpacingToken } from "./responsive";
import type { BoxProps } from "./Box";

type SpacingInput = SpacingToken | string | number;

export interface FlexProps extends BoxProps {
  /** Flex direction */
  direction?: ResponsiveValue<
    "row" | "column" | "row-reverse" | "column-reverse"
  >;
  /** Flex wrap */
  wrap?: ResponsiveValue<"nowrap" | "wrap" | "wrap-reverse">;
  /** Gap between children (spacing token, px number, or CSS string) */
  gap?: ResponsiveValue<SpacingInput>;
  /** Row gap (overrides gap for rows) */
  rowGap?: ResponsiveValue<SpacingInput>;
  /** Column gap (overrides gap for columns) */
  columnGap?: ResponsiveValue<SpacingInput>;
  /** Align items (cross-axis) */
  align?: ResponsiveValue<
    "flex-start" | "flex-end" | "center" | "baseline" | "stretch"
  >;
  /** Justify content (main-axis) */
  justify?: ResponsiveValue<
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly"
  >;
  /** Shorthand: set both direction="column" */
  column?: boolean;
  /** Shorthand: center both axes */
  center?: boolean;
  /** Make flex container inline */
  inline?: boolean;
}

export const Flex = memo(
  forwardRef<HTMLDivElement, FlexProps>(function Flex(
    {
      direction,
      wrap,
      gap,
      rowGap,
      columnGap,
      align,
      justify,
      column = false,
      center = false,
      inline = false,
      style,
      children,
      // Extract BoxProps we handle ourselves
      padding,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      margin,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      background,
      border,
      borderRadius,
      shadow,
      display: _display,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      overflow,
      position,
      flex: flexProp,
      flexGrow,
      flexShrink,
      as: Component = "div",
      ...rest
    },
    ref,
  ): React.ReactElement {
    const { tokens } = useTheme();
    const { breakpoint } = useBreakpoint();

    const resolveSpacing = (val: ResponsiveValue<SpacingInput> | undefined) =>
      resolveResponsiveSpacing(val, breakpoint, tokens);

    // Resolve padding
    const paddingAll = resolveSpacing(padding ?? p);
    const paddingXVal = resolveSpacing(px);
    const paddingYVal = resolveSpacing(py);

    // Resolve margin
    const marginAll = resolveSpacing(margin ?? m);
    const marginXVal = resolveSpacing(mx);
    const marginYVal = resolveSpacing(my);

    // Background color resolution
    const bgMap: Record<string, string> = {
      base: tokens.colors.background.base,
      surface: tokens.colors.background.surface,
      elevated: tokens.colors.background.elevated,
      overlay: tokens.colors.background.overlay,
    };

    const resolveSize = (
      val: string | number | undefined,
    ): string | undefined => {
      if (val === undefined) return undefined;
      if (typeof val === "number") return `${val}px`;
      return val;
    };

    const resolvedDirection = column
      ? "column"
      : (resolveResponsive(direction, breakpoint) ?? "row");

    // Resolve gap values — use conditional spreads to avoid setting
    // rowGap/columnGap to undefined, which clears the gap shorthand
    // in browsers (setting `style.rowGap = ''` removes the value
    // that `style.gap` set for row-gap).
    const resolvedGap = resolveSpacing(gap);
    const resolvedRowGap = resolveSpacing(rowGap);
    const resolvedColumnGap = resolveSpacing(columnGap);

    const computedStyle: React.CSSProperties = {
      display: inline ? "inline-flex" : "flex",
      flexDirection: resolvedDirection,
      flexWrap: resolveResponsive(wrap, breakpoint),
      ...(resolvedGap !== undefined ? { gap: resolvedGap } : {}),
      ...(resolvedRowGap !== undefined ? { rowGap: resolvedRowGap } : {}),
      ...(resolvedColumnGap !== undefined
        ? { columnGap: resolvedColumnGap }
        : {}),
      alignItems: center ? "center" : resolveResponsive(align, breakpoint),
      justifyContent: center
        ? "center"
        : resolveResponsive(justify, breakpoint),

      // Padding
      ...(paddingAll ? { padding: paddingAll } : {}),
      ...(paddingXVal
        ? { paddingLeft: paddingXVal, paddingRight: paddingXVal }
        : {}),
      ...(paddingYVal
        ? { paddingTop: paddingYVal, paddingBottom: paddingYVal }
        : {}),
      ...(resolveSpacing(pt) ? { paddingTop: resolveSpacing(pt) } : {}),
      ...(resolveSpacing(pr) ? { paddingRight: resolveSpacing(pr) } : {}),
      ...(resolveSpacing(pb) ? { paddingBottom: resolveSpacing(pb) } : {}),
      ...(resolveSpacing(pl) ? { paddingLeft: resolveSpacing(pl) } : {}),

      // Margin
      ...(marginAll ? { margin: marginAll } : {}),
      ...(marginXVal
        ? { marginLeft: marginXVal, marginRight: marginXVal }
        : {}),
      ...(marginYVal
        ? { marginTop: marginYVal, marginBottom: marginYVal }
        : {}),
      ...(resolveSpacing(mt) ? { marginTop: resolveSpacing(mt) } : {}),
      ...(resolveSpacing(mr) ? { marginRight: resolveSpacing(mr) } : {}),
      ...(resolveSpacing(mb) ? { marginBottom: resolveSpacing(mb) } : {}),
      ...(resolveSpacing(ml) ? { marginLeft: resolveSpacing(ml) } : {}),

      // Visual
      ...(background
        ? { backgroundColor: bgMap[background] || background }
        : {}),
      ...(border
        ? {
            border:
              border === true
                ? `1px solid ${tokens.colors.border.default}`
                : (border as string),
          }
        : {}),
      ...(borderRadius
        ? {
            borderRadius:
              (tokens.borderRadius as Record<string, string>)[
                borderRadius as string
              ] || borderRadius,
          }
        : {}),
      ...(shadow && shadow !== "glow"
        ? {
            boxShadow:
              (tokens.shadows as unknown as Record<string, string>)[
                shadow as string
              ] || shadow,
          }
        : {}),

      // Size
      width: resolveSize(resolveResponsive(width, breakpoint)),
      height: resolveSize(resolveResponsive(height, breakpoint)),
      minWidth: resolveSize(minWidth),
      minHeight: resolveSize(minHeight),
      maxWidth: resolveSize(maxWidth),
      maxHeight: resolveSize(maxHeight),
      overflow,
      position,
      flex: flexProp,
      flexGrow,
      flexShrink,

      ...style,
    };

    return (
      <Component ref={ref} style={computedStyle} {...rest}>
        {children}
      </Component>
    );
  }),
);

export default Flex;
